import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { FormTextFieldDS } from 'components/reusable/form/FormFields'
import { Button, Checkbox } from 'slab'
import { getGetAdminEmployersEmployerIdComplianceAuditsQueryKey, getGetAdminEmployersEmployerIdComplianceOwnersQueryKey, usePostAdminComplianceOwners, usePutAdminComplianceOwnersOwnerId } from 'utils/web_api'
import { type AdminComplianceOwner } from 'utils/web_api.schemas'

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet'

const CONTROLLING_OFFICER_DESCRIPTION = 'A controlling officer has direct influence over day-to-day functions of the business. This can be a CEO, CFO, manager, or anyone in a similar role.'
const BENEFICIAL_OWNER_DESCRIPTION = 'A beneficial owner is someone who owns 25% or more of the business equity.'
const ENGINEERING_CONTACT_DESCRIPTION = 'Engineering needs to be contacted to update this value.'

// Custom checkbox component that follows FormFieldDS pattern
interface FormCheckboxFieldDSProps {
  fieldId: string
  fieldLabel: string
  description?: string
  register: any
  validationRule?: Record<string, any>
  error?: any
  disabled?: boolean
}

const FormCheckboxFieldDS = ({
  fieldId,
  fieldLabel,
  description,
  register,
  validationRule,
  error,
  disabled,
}: FormCheckboxFieldDSProps) => {
  return (
    <Checkbox
      name={fieldId}
      label={fieldLabel}
      description={description}
      error={error?.message}
      disabled={disabled}
      {...register(fieldId, {
        ...validationRule,
      })}
    />
  )
}

type ComplianceOwnerFormData = Omit<AdminComplianceOwner, 'is_controlling_officer' | 'is_beneficial_owner'> & {
  is_controlling_officer?: boolean
  is_beneficial_owner?: boolean
}

interface EmployerComplianceOwnerSheetProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  setSelectedOwner: (owner: AdminComplianceOwner | undefined) => void
  employerId: string
  owner?: AdminComplianceOwner
}

const defaultValues: ComplianceOwnerFormData = {
  id: '',
  employer_id: '',
  first_name: '',
  last_name: '',
  email: '',
  is_controlling_officer: undefined,
  is_beneficial_owner: undefined,
  title: '',
}

const EmployerComplianceOwnerSheet = ({
  isOpen,
  setIsOpen,
  setSelectedOwner,
  employerId,
  owner,
}: EmployerComplianceOwnerSheetProps) => {
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<AdminComplianceOwner>({
    defaultValues: owner
      ? {
          id: owner.id,
          employer_id: owner.employer_id,
          first_name: owner.first_name || '',
          last_name: owner.last_name || '',
          email: owner.email || '',
          is_controlling_officer: owner.is_controlling_officer,
          is_beneficial_owner: owner.is_beneficial_owner,
          title: owner.title || '',
        }
      : defaultValues,
  })

  // Reset form when owner changes
  useEffect(() => {
    if (owner) {
      reset({
        id: owner.id,
        employer_id: owner.employer_id,
        first_name: owner.first_name || '',
        last_name: owner.last_name || '',
        email: owner.email || '',
        is_controlling_officer: owner.is_controlling_officer,
        is_beneficial_owner: owner.is_beneficial_owner,
        title: owner.title || '',
      })
    } else {
      reset(defaultValues)
    }
  }, [owner, reset])

  const createOwnerMutation = usePostAdminComplianceOwners({
    mutation: {
      onSuccess: (newOwner) => {
        // Update the cache with the new owner
        queryClient.setQueryData(
          getGetAdminEmployersEmployerIdComplianceOwnersQueryKey(employerId),
          (oldData: AdminComplianceOwner[] | undefined) => {
            if (!oldData) return [newOwner]
            return [...oldData, newOwner]
          }
        )
        queryClient.invalidateQueries({
          queryKey: getGetAdminEmployersEmployerIdComplianceAuditsQueryKey(employerId)
        })
        toast.success('Owner created successfully')
        setIsOpen(false)
        setSelectedOwner(undefined)
        reset()
      },
      onError: (error: any) => {
        console.error('Failed to create owner:', error.message)
        toast.error(error.message)
      }
    }
  })

  const updateOwnerMutation = usePutAdminComplianceOwnersOwnerId({
    mutation: {
      onSuccess: (updatedOwner) => {
        // Update the cache with the updated owner
        queryClient.setQueryData(
          getGetAdminEmployersEmployerIdComplianceOwnersQueryKey(employerId),
          (oldData: AdminComplianceOwner[] | undefined) => {
            if (!oldData) return [updatedOwner]
            return oldData.map(owner => owner.id === updatedOwner.id ? updatedOwner : owner)
          }
        )
        queryClient.invalidateQueries({
          queryKey: getGetAdminEmployersEmployerIdComplianceAuditsQueryKey(employerId)
        })
        toast.success('Owner updated successfully')
        setIsOpen(false)
        setSelectedOwner(undefined)
        reset()
      },
      onError: (error: any) => {
        console.error('Failed to update owner:', error.message)
        toast.error(error.message)
      }
    }
  })

  const onSubmit = (data: AdminComplianceOwner) => {
    if (owner) {
      // For updates, only send the fields that can be changed
      const updateData = {
        email: data.email,
        title: data.title,
      }
      updateOwnerMutation.mutate({ ownerId: owner.id, data: updateData })
    } else {
      // For creates, send all required fields
      const createData = {
        employer_id: employerId,
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        is_controlling_officer: data.is_controlling_officer || false,
        is_beneficial_owner: data.is_beneficial_owner || false,
        title: data.title,
      }
      createOwnerMutation.mutate({ data: createData })
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className='h-dvh overflow-y-auto' style={{ minWidth: '500px' }} data-sheet='add-owner'>
        <SheetHeader>
          <SheetTitle className='heading-lg'>
            {owner ? 'Edit Owner' : 'Add an Owner'}
          </SheetTitle>
        </SheetHeader>
        <SheetDescription className='mb-4'>
          {owner ? 'Edit the owner details below.' : 'Enter details about the Owner you want to add below.'}
        </SheetDescription>

        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          <FormTextFieldDS
            fieldId='first_name'
            fieldLabel='First Name'
            placeholder='Enter first name'
            register={register}
            validationRule={{ required: 'First name is required' }}
            error={errors.first_name}
            disabled={!!owner}
            description={owner ? ENGINEERING_CONTACT_DESCRIPTION : undefined}
          />

          <FormTextFieldDS
            fieldId='last_name'
            fieldLabel='Last Name'
            placeholder='Enter last name'
            register={register}
            validationRule={{ required: 'Last name is required' }}
            error={errors.last_name}
            disabled={!!owner}
            description={owner ? ENGINEERING_CONTACT_DESCRIPTION : undefined}
          />

          <FormTextFieldDS
            fieldId='email'
            fieldLabel='Email'
            placeholder='Enter email address'
            register={register}
            validationRule={{
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            }}
            error={errors.email}
          />

          <FormTextFieldDS
            fieldId='title'
            fieldLabel='Title'
            placeholder='CEO, Manager, Engineer, etc.'
            register={register}
            validationRule={{ required: 'Title is required' }}
            error={errors.title}
          />

          <div className='space-y-2 pt-4'>
            <FormCheckboxFieldDS
              fieldId='is_controlling_officer'
              fieldLabel='Is this person a controlling officer?'
              description={
                owner
                  ? CONTROLLING_OFFICER_DESCRIPTION + ' ' + ENGINEERING_CONTACT_DESCRIPTION
                  : CONTROLLING_OFFICER_DESCRIPTION
              }
              register={register}
              error={errors.is_controlling_officer}
              disabled={!!owner}
            />
          </div>

          <div className='space-y-2 pt-4'>
            <FormCheckboxFieldDS
              fieldId='is_beneficial_owner'
              fieldLabel='Is this person a beneficial owner?'
              description={
                owner
                  ? BENEFICIAL_OWNER_DESCRIPTION + ' ' + ENGINEERING_CONTACT_DESCRIPTION
                  : BENEFICIAL_OWNER_DESCRIPTION
              }
              register={register}
              validationRule={{
                validate: (value: boolean, formValues: any) => {
                  // If this checkbox is checked, validation passes
                  if (value) return true

                  // If this checkbox is unchecked, check if controlling officer is checked
                  if (formValues.is_controlling_officer) return true

                  // If both are unchecked, show error
                  return 'Please select at least one owner type (controlling officer or beneficial owner)'
                }
              }}
              error={errors.is_beneficial_owner}
              disabled={!!owner}
            />
          </div>

          <div className='flex justify-end gap-3 pt-4'>
            <Button
              variant='secondary'
              onClick={() => {
                setIsOpen(false)
                setSelectedOwner(undefined)
              }}
            >
              Cancel
            </Button>

            <Button
              data-testid={owner ? 'update-owner-button' : 'create-owner-button'}
              type='submit'
              variant='primary'
              disabled={!isValid || createOwnerMutation.isPending || updateOwnerMutation.isPending}
            >
              {owner
                ? (updateOwnerMutation.isPending ? 'Updating...' : 'Update Owner')
                : (createOwnerMutation.isPending ? 'Creating...' : 'Create Owner')}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}

export default EmployerComplianceOwnerSheet
